import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabaseClient';

const DICTIONARY_TIMEOUT_MS = 8000;
const SERVICE_UNAVAILABLE_MESSAGE =
  'The dictionary service is unavailable right now. Please try again in a moment.';

// Fails fast instead of leaving the user on a spinner when an upstream host hangs.
const fetchWithTimeout = async (url, timeoutMs = DICTIONARY_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

// A hung host, a CORS-less error page (e.g. Cloudflare 5xx) or a DNS failure all
// surface as a TypeError/AbortError rather than a readable HTTP status.
const isServiceFailure = (err) =>
  err.name === 'AbortError' || err.name === 'TimeoutError' || err instanceof TypeError;

// Wiktionary returns definitions as HTML fragments.
const stripHtml = (html) =>
  String(html)
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const parseDictionaryApiResponse = (data) => {
  const meanings = [];
  (data?.[0]?.meanings || []).forEach((entry) => {
    (entry.definitions || []).forEach((definition) => {
      meanings.push({
        definition: definition.definition,
        partOfSpeech: entry.partOfSpeech,
        example: definition.example || null
      });
    });
  });
  return meanings;
};

const parseWiktionaryResponse = (data) => {
  const meanings = [];
  (data?.en || []).forEach((entry) => {
    (entry.definitions || []).forEach((definition) => {
      const text = stripHtml(definition.definition);
      if (!text) return;
      const rawExample = definition.parsedExamples?.[0]?.example || definition.examples?.[0];
      meanings.push({
        definition: text,
        partOfSpeech: (entry.partOfSpeech || '').toLowerCase(),
        example: rawExample ? stripHtml(rawExample) : null
      });
    });
  });
  return meanings;
};

// While the primary source is down, skip it rather than making every lookup wait
// out the full timeout first. Re-probed once the cooldown lapses.
const PRIMARY_COOLDOWN_MS = 60000;

// Each source resolves to { status: 'ok' | 'notfound' } and throws only when the
// service itself is broken, so the caller can tell "no such word" from "try elsewhere".
const fetchFromDictionaryApi = async (wordToFetch) => {
  const response = await fetchWithTimeout(
    `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(wordToFetch)}`
  );
  if (response.status === 404) return { status: 'notfound' };
  if (!response.ok) throw new Error(`Dictionary API error: ${response.status}`);
  return { status: 'ok', meanings: parseDictionaryApiResponse(await response.json()) };
};

const fetchFromWiktionary = async (wordToFetch) => {
  const response = await fetchWithTimeout(
    `https://en.wiktionary.org/api/rest_v1/page/definition/${encodeURIComponent(wordToFetch)}`
  );
  if (response.status === 404) return { status: 'notfound' };
  if (!response.ok) throw new Error(`Wiktionary error: ${response.status}`);
  return { status: 'ok', meanings: parseWiktionaryResponse(await response.json()) };
};

function WordInput({ addWord, getMeaning, userEmail, userId }) {
  const [word, setWord] = useState('');
  const [meaning, setMeaning] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [relatedWords, setRelatedWords] = useState([]);
  const [savedWords, setSavedWords] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [showMeanings, setShowMeanings] = useState(false);
  const [removeMode, setRemoveMode] = useState(false);
  const [selectedWords, setSelectedWords] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [multipleMeanings, setMultipleMeanings] = useState([]);
  const [showMeaningSelector, setShowMeaningSelector] = useState(false);
  const [selectedMeaning, setSelectedMeaning] = useState('');
  const [generatingDistractors, setGeneratingDistractors] = useState(false);
  const [generatingMissingDistractors, setGeneratingMissingDistractors] = useState(false);
  const [missingDistractorsProgress, setMissingDistractorsProgress] = useState({ current: 0, total: 0 });
  const primaryUnavailableUntil = useRef(0);

  const fetchSavedWords = useCallback(async () => {
    try {
      if (!userId) return;

      let query = supabase.from('words').select('*').order('created_at', { ascending: false });
      let { data, error } = await query.eq('user_id', userId);

      if (error || !data) {
        query = supabase.from('words').select('*').order('created_at', { ascending: false });
        ({ data, error } = await query.eq('user_id', userId));
      }

      if (error || !data) {
        query = supabase.from('words').select('*');
        ({ data, error } = await query.eq('user_id', userId));
      }

      if (error) throw new Error(error.message);
      setSavedWords(data || []);
    } catch (err) {}
  }, [userId]);

  const deleteSelectedWords = async () => {
    try {
      for (const wordId of selectedWords) {
        const { error } = await supabase.from('words').delete().eq('id', wordId);
        if (error) throw new Error(error.message);
      }
      setSavedWords(prevWords => prevWords.filter(word => !selectedWords.includes(word.id)));
      setSelectedWords([]);
      setRemoveMode(false);
      setShowConfirmModal(false);
      setSuccessMessage(`${selectedWords.length} word${selectedWords.length === 1 ? '' : 's'} removed successfully.`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to remove words: ' + err.message);
    }
  };

  const toggleWordSelection = (wordId) => {
    setSelectedWords(prev =>
      prev.includes(wordId) ? prev.filter(id => id !== wordId) : [...prev, wordId]
    );
  };

  const selectAllWords = () => setSelectedWords(savedWords.map(word => word.id));
  const deselectAllWords = () => setSelectedWords([]);
  const exitRemoveMode = () => { setRemoveMode(false); setSelectedWords([]); };

  const handleMeaningSelection = (meaningObj) => {
    setSelectedMeaning(meaningObj.definition);
    setMeaning(meaningObj.definition);
    setShowMeaningSelector(false);
  };

  const generateDistractors = async (word, correctMeaning) => {
    setGeneratingDistractors(true);
    setError('');
    try {
      const healthResponse = await fetch('/api/health');
      if (!healthResponse.ok) throw new Error(`API server is not responding properly: ${healthResponse.status}`);
      const healthData = await healthResponse.json();
      if (!healthData.apiKeyConfigured) throw new Error('Anthropic API key is not configured on the server');

      const response = await fetch('/api/generate-distractors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ word, correctMeaning })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const data = await response.json();
      return data.distractors;
    } catch (err) {
      if (err.message.includes('fetch')) {
        setError('Cannot connect to API server. Please make sure the server is running.');
      } else {
        setError('Failed to generate distractors: ' + err.message);
      }
      return null;
    } finally {
      setGeneratingDistractors(false);
    }
  };

  const generateMissingDistractors = async () => {
    const wordsNeedingDistractors = savedWords.filter(w => !w.distractors || w.distractors.length < 3);
    if (wordsNeedingDistractors.length === 0) return;

    setGeneratingMissingDistractors(true);
    setMissingDistractorsProgress({ current: 0, total: wordsNeedingDistractors.length });
    let successCount = 0;

    for (let i = 0; i < wordsNeedingDistractors.length; i++) {
      const wordEntry = wordsNeedingDistractors[i];
      setMissingDistractorsProgress({ current: i + 1, total: wordsNeedingDistractors.length });
      try {
        const response = await fetch('/api/generate-distractors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ word: wordEntry.word, correctMeaning: wordEntry.meaning })
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `API error: ${response.status}`);
        }
        const data = await response.json();
        const distractors = data.distractors;
        if (!distractors || distractors.length < 3) continue;
        const { data: updatedRows, error: dbError } = await supabase
          .from('words')
          .update({ distractors })
          .eq('id', wordEntry.id)
          .select();
        if (dbError) throw new Error(`Database error: ${dbError.message}`);
        if (!updatedRows || updatedRows.length === 0) throw new Error('Supabase UPDATE was silently blocked. Add an UPDATE policy to the words table in Supabase RLS settings.');
        successCount++;
        setSavedWords(prev => prev.map(w => w.id === wordEntry.id ? { ...w, distractors } : w));
      } catch (err) {
        setGeneratingMissingDistractors(false);
        setMissingDistractorsProgress({ current: 0, total: 0 });
        setError(`Failed to generate distractors: ${err.message}`);
        return;
      }
    }

    setGeneratingMissingDistractors(false);
    setMissingDistractorsProgress({ current: 0, total: 0 });
    setSuccessMessage(`Generated distractors for ${successCount} of ${wordsNeedingDistractors.length} word${wordsNeedingDistractors.length === 1 ? '' : 's'}.`);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleSave = async () => {
    if (word && (meaning || selectedMeaning)) {
      try {
        const meaningToSave = meaning || selectedMeaning;
        const distractors = await generateDistractors(word, meaningToSave);
        await addWord(word, meaningToSave, distractors || []);
        setWord('');
        setMeaning('');
        setSelectedMeaning('');
        setMultipleMeanings([]);
        setShowMeaningSelector(false);
        setError('');
        if (distractors && distractors.length > 0) {
          setSuccessMessage(`Word saved with ${distractors.length} distractors.`);
        } else {
          setSuccessMessage('Word saved. Distractors could not be generated — check if the API server is running.');
        }
        setTimeout(() => setSuccessMessage(''), 3000);
        await fetchSavedWords();
      } catch (err) {
        setError('Failed to save word: ' + err.message);
      }
    }
  };

  useEffect(() => {
    if (userId) fetchSavedWords();
    else setSavedWords([]);
  }, [userId, fetchSavedWords]);

  useEffect(() => {
    if (userId && savedWords.length === 0) fetchSavedWords();
  }, [userId, savedWords.length, fetchSavedWords]);

  const fetchMeaning = async (wordToFetch = word) => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    setMeaning('');
    setRelatedWords([]);
    setMultipleMeanings([]);
    setShowMeaningSelector(false);
    setSelectedMeaning('');

    try {
      let result;
      const primaryInCooldown = Date.now() < primaryUnavailableUntil.current;
      try {
        if (primaryInCooldown) throw new Error('Dictionary API is in cooldown after a recent failure');
        result = await fetchFromDictionaryApi(wordToFetch);
      } catch (primaryErr) {
        // The primary source is down or unreachable — fall back to Wiktionary so
        // an outage there doesn't take the whole feature offline.
        if (!primaryInCooldown && isServiceFailure(primaryErr)) {
          primaryUnavailableUntil.current = Date.now() + PRIMARY_COOLDOWN_MS;
        }
        try {
          result = await fetchFromWiktionary(wordToFetch);
        } catch (fallbackErr) {
          setError(SERVICE_UNAVAILABLE_MESSAGE);
          return;
        }
      }

      if (result.status === 'notfound') {
        const related = await getRelatedWords(wordToFetch);
        if (related.length > 0) {
          setRelatedWords(related);
          setError('Word not found. Did you mean:');
        } else {
          setError('Word not found, and no related words were found.');
        }
        return;
      }

      const meanings = result.meanings;
      if (meanings.length > 0) {
        if (meanings.length === 1) {
          setMeaning(meanings[0].definition);
        } else {
          setMultipleMeanings(meanings);
          setShowMeaningSelector(true);
        }
      } else {
        setError('No definitions found for this word.');
      }
    } catch (err) {
      setError(isServiceFailure(err) ? SERVICE_UNAVAILABLE_MESSAGE : err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRelatedWords = async (word) => {
    try {
      const response = await fetchWithTimeout(
        `https://api.datamuse.com/words?sp=${encodeURIComponent(word)}&max=5`
      );
      if (!response.ok) throw new Error('Failed to fetch related words');
      const data = await response.json();
      return data.map((item) => item.word);
    } catch (error) {
      return [];
    }
  };

  const wordsWithoutDistractors = savedWords.filter(w => !w.distractors || w.distractors.length < 3);

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-lattice-dark">Word Input</h2>
        <div className="flex gap-2 items-center">
          <Link
            to="/quiz"
            className="text-sm text-lattice-muted hover:text-lattice-dark transition-colors"
          >
            Quiz Mode
          </Link>
        </div>
      </div>

      {/* Word Search */}
      <div className="mb-8">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSuccessMessage('');
            fetchMeaning();
          }}
          className="flex gap-3 mb-4"
        >
          <input
            type="text"
            placeholder="Enter a word"
            value={word}
            onChange={(e) => { setWord(e.target.value); setSuccessMessage(''); }}
            required
            className="flex-1 px-4 py-2.5 text-base border border-lattice-card-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-lattice-green focus:border-transparent transition-shadow"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2.5 bg-lattice-green hover:bg-lattice-green-hover text-lattice-btn-text rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Fetching...' : 'Get Meaning'}
          </button>
        </form>

        {/* Error / suggestions */}
        {error && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg mb-3">
            <p className="text-red-700 text-sm">{error}</p>
            {relatedWords.length > 0 && (
              <p className="mt-2 text-sm text-red-700">
                Did you mean:{' '}
                {relatedWords.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      setWord(suggestion);
                      setRelatedWords([]);
                      setMeaning('');
                      setError('');
                      setSuccessMessage('');
                      fetchMeaning(suggestion);
                    }}
                    className="text-lattice-green underline font-medium mr-2 hover:text-lattice-green-hover transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </p>
            )}
          </div>
        )}

        {/* Distractor generation loading */}
        {generatingDistractors && (
          <div className="flex items-center gap-3 px-4 py-3 bg-lattice-bg border border-lattice-card-border rounded-lg mb-3">
            <div className="w-4 h-4 border-2 border-lattice-green border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <p className="text-lattice-muted text-sm">Generating distractors using AI...</p>
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="px-4 py-3 bg-green-50 border border-green-200 rounded-lg mb-3">
            <p className="text-green-800 text-sm">{successMessage}</p>
          </div>
        )}

        {/* Multiple meanings selector */}
        {showMeaningSelector && multipleMeanings.length > 0 && (
          <div className="mt-4 p-5 bg-lattice-bg border border-lattice-card-border rounded-xl">
            <h4 className="text-lattice-dark font-semibold text-base mb-4">Choose a meaning for "{word}":</h4>
            <div className="max-h-72 overflow-y-auto space-y-2 mb-4">
              {multipleMeanings.map((meaningObj, index) => (
                <div
                  key={index}
                  onClick={() => handleMeaningSelection(meaningObj)}
                  className={`p-4 bg-white border rounded-lg cursor-pointer transition-all ${
                    selectedMeaning === meaningObj.definition
                      ? 'border-lattice-green ring-1 ring-lattice-green'
                      : 'border-lattice-card-border hover:border-lattice-green'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="bg-lattice-green text-lattice-btn-text text-xs font-medium px-2.5 py-1 rounded-full capitalize">
                      {meaningObj.partOfSpeech}
                    </span>
                    <span className="text-gray-400 text-xs">Option {index + 1}</span>
                  </div>
                  <p className="text-lattice-dark text-sm leading-relaxed mb-1">{meaningObj.definition}</p>
                  {meaningObj.example && (
                    <p className="text-lattice-muted text-xs italic">
                      Example: "{meaningObj.example}"
                    </p>
                  )}
                </div>
              ))}
            </div>

            {selectedMeaning && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
                <p className="text-green-900 text-sm font-semibold mb-1">Selected:</p>
                <p className="text-green-800 text-sm">{selectedMeaning}</p>
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={!selectedMeaning || generatingDistractors}
              className="px-5 py-2.5 bg-lattice-green hover:bg-lattice-green-hover text-lattice-btn-text rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generatingDistractors ? 'Generating distractors...' : 'Save selected meaning'}
            </button>
          </div>
        )}

        {/* Single meaning display */}
        {meaning && !showMeaningSelector && (
          <div className="mt-4 p-4 bg-lattice-bg border border-lattice-card-border rounded-xl flex items-start justify-between gap-4">
            <p className="text-lattice-dark text-sm leading-relaxed">
              <span className="font-semibold">Meaning:</span> {meaning}
            </p>
            <button
              onClick={handleSave}
              disabled={generatingDistractors}
              className="flex-shrink-0 px-4 py-2 bg-lattice-green hover:bg-lattice-green-hover text-lattice-btn-text rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generatingDistractors ? 'Generating...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Saved Words Section */}
      <div className="bg-white border border-lattice-card-border rounded-xl p-5">
        {/* Section header */}
        <div className="flex justify-between items-center mb-5 pb-4 border-b border-lattice-card-border flex-wrap gap-3">
          <h3 className="text-lg font-semibold text-lattice-dark">My Words</h3>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Toggle meanings */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-lattice-muted">Show meanings</span>
              <button
                onClick={() => setShowMeanings(!showMeanings)}
                className={`relative inline-flex w-10 h-5 rounded-full transition-colors focus:outline-none ${
                  showMeanings ? 'bg-lattice-green' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block w-4 h-4 bg-white rounded-full shadow transition-transform mt-0.5 ${
                    showMeanings ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Generate missing distractors */}
            {!removeMode && wordsWithoutDistractors.length > 0 && (
              <button
                onClick={generateMissingDistractors}
                disabled={generatingMissingDistractors}
                className="px-4 py-1.5 border border-lattice-green text-lattice-green hover:bg-lattice-green hover:text-lattice-btn-text rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generatingMissingDistractors
                  ? `Generating ${missingDistractorsProgress.current}/${missingDistractorsProgress.total}...`
                  : `Generate missing distractors (${wordsWithoutDistractors.length})`}
              </button>
            )}

            {/* Remove mode controls */}
            {!removeMode ? (
              <button
                onClick={() => setRemoveMode(true)}
                className="px-4 py-1.5 border border-red-300 text-red-500 hover:bg-red-500 hover:text-white rounded-lg text-sm font-medium transition-colors"
              >
                Remove words
              </button>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-red-500">{selectedWords.length} selected</span>
                <button onClick={selectAllWords} className="px-3 py-1.5 bg-lattice-green hover:bg-lattice-green-hover text-lattice-btn-text rounded text-xs font-medium transition-colors">
                  Select all
                </button>
                <button onClick={deselectAllWords} className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-lattice-dark rounded text-xs font-medium transition-colors">
                  Deselect all
                </button>
                <button
                  onClick={() => setShowConfirmModal(true)}
                  disabled={selectedWords.length === 0}
                  className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Remove selected
                </button>
                <button onClick={exitRemoveMode} className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-lattice-dark rounded text-xs font-medium transition-colors">
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Word list */}
        {savedWords.length > 0 ? (
          <ul className="space-y-2">
            {savedWords.map((savedWord) => (
              <li
                key={savedWord.id}
                className="relative p-4 bg-white border border-lattice-card-border rounded-lg"
              >
                {removeMode && (
                  <input
                    type="checkbox"
                    checked={selectedWords.includes(savedWord.id)}
                    onChange={() => toggleWordSelection(savedWord.id)}
                    className="absolute top-4 left-4 w-4 h-4 cursor-pointer accent-lattice-green"
                  />
                )}
                <div className={removeMode ? 'ml-7' : ''}>
                  <strong className="text-gray-500 text-sm font-semibold block">
                    {savedWord.word}
                  </strong>
                  {showMeanings && (
                    <div className="mt-2 space-y-2">
                      <p className="text-lattice-muted text-sm leading-relaxed">
                        {savedWord.meaning}
                      </p>
                      {savedWord.distractors && savedWord.distractors.length > 0 && (
                        <div>
                          <p className="text-lattice-dark text-xs font-medium mb-1">
                            Distractors ({savedWord.distractors.length}):
                          </p>
                          <ul className="list-disc pl-5 text-lattice-muted text-xs space-y-0.5">
                            {savedWord.distractors.map((distractor, index) => (
                              <li key={index}>{distractor}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-12 text-lattice-muted">
            <p className="text-sm">No words saved yet. Start by searching for a word above.</p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 px-4">
          <div className="bg-white rounded-xl p-8 max-w-sm w-full text-center shadow-xl border border-lattice-card-border">
            <h3 className="text-lattice-dark text-xl font-bold mb-4">Confirm removal</h3>
            <p className="text-lattice-muted text-sm leading-relaxed mb-2">
              Are you sure you want to remove {selectedWords.length} word{selectedWords.length === 1 ? '' : 's'}?
            </p>
            <p className="text-gray-400 text-xs mb-6">This action cannot be undone.</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-5 py-2.5 border border-lattice-card-border text-lattice-muted hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteSelectedWords}
                className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WordInput;
